import _ from 'lodash';
import moment from 'moment';
import { ADMIN_SCOPES } from '../constants/admin_scope_constants';

export const OFFICE_SPACE_TYPES = {
  BOARDROOM: 'boardroom',
  CONFERENCE_ROOM: 'conference_room',
  SMALL_OFFICE: 'small_office',
  PHONE_BOOTH: 'phone_booth',
  DESK: 'desk',
  OPEN_SEATING: 'open_seating',
  LABEL: 'label'
};

export const OFFICE_DOOR_STATES = {
  OPEN: 'open',
  CLOSED: 'closed'
};

export const DOOR_STATE_INFO = {
  [OFFICE_DOOR_STATES.OPEN]: { label: 'Open', iconKey: 'open', tooltip: 'Always-on video stays open — anyone can join.' },
  [OFFICE_DOOR_STATES.CLOSED]: { label: 'Closed', iconKey: 'closed', tooltip: 'Always-on video is turned off. Bring your own meeting link.' }
};

export const SPACE_TYPE_LABELS = {
  [OFFICE_SPACE_TYPES.BOARDROOM]: 'Boardroom',
  [OFFICE_SPACE_TYPES.CONFERENCE_ROOM]: 'Conference Room',
  [OFFICE_SPACE_TYPES.SMALL_OFFICE]: 'Small Office',
  [OFFICE_SPACE_TYPES.PHONE_BOOTH]: 'Phone Booth',
  [OFFICE_SPACE_TYPES.DESK]: 'Desk',
  [OFFICE_SPACE_TYPES.OPEN_SEATING]: 'Open Seating',
  [OFFICE_SPACE_TYPES.LABEL]: 'Label'
};

export const SPACE_TYPE_DEFAULTS = {
  [OFFICE_SPACE_TYPES.BOARDROOM]: { default_duration_min: 60, duration_options: [30, 60, 120], has_door: true },
  [OFFICE_SPACE_TYPES.CONFERENCE_ROOM]: { default_duration_min: 30, duration_options: [30, 60, 120], has_door: true },
  [OFFICE_SPACE_TYPES.SMALL_OFFICE]: { default_duration_min: 30, duration_options: [30, 60, 120], has_door: false },
  [OFFICE_SPACE_TYPES.PHONE_BOOTH]: { default_duration_min: 30, duration_options: [30, 60], has_door: false },
  [OFFICE_SPACE_TYPES.DESK]: { default_duration_min: null, duration_options: ['am', 'pm', 'full_day'], has_door: false },
  [OFFICE_SPACE_TYPES.OPEN_SEATING]: { default_duration_min: null, duration_options: ['am', 'pm', 'full_day'], has_door: false },
  [OFFICE_SPACE_TYPES.LABEL]: { default_duration_min: null, duration_options: [], has_door: false }
};

export const OFFICE_SCOPE_LEVELS = [
  { scope: ADMIN_SCOPES.OFFICE_ADMIN, label: 'Admin', max_advance_days: 90, can_manage: true, can_book_boardroom: true },
  { scope: ADMIN_SCOPES.OFFICE_EXEC, label: 'Executive', max_advance_days: 30, can_manage: false, can_book_boardroom: true },
  { scope: ADMIN_SCOPES.OFFICE_MANAGER, label: 'Manager', max_advance_days: 14, can_manage: false, can_book_boardroom: true },
  { scope: ADMIN_SCOPES.OFFICE_STANDARD, label: 'Standard', max_advance_days: 7, can_manage: false, can_book_boardroom: false }
];

const OFFICE_SCOPE_HIERARCHY = [ADMIN_SCOPES.OFFICE_ADMIN, ADMIN_SCOPES.OFFICE_EXEC, ADMIN_SCOPES.OFFICE_MANAGER, ADMIN_SCOPES.OFFICE_STANDARD];

export const getAdditiveScopesForLevel = level => {
  const idx = OFFICE_SCOPE_HIERARCHY.indexOf(level);
  if (idx === -1) return [];
  return OFFICE_SCOPE_HIERARCHY.slice(idx);
};

const parseScopes = user => {
  const raw = user?.scopes;
  if (!raw) return [];
  return Array.isArray(raw) ? raw : raw.split(',').map(s => s.trim());
};

export const getOfficePermissions = user => {
  const scopes = parseScopes(user);
  for (const level of OFFICE_SCOPE_LEVELS) {
    if (scopes.includes(level.scope)) return level;
  }
  return null;
};

export const isOfficeAdminOrExec = user => {
  const scopes = parseScopes(user);
  return scopes.includes(ADMIN_SCOPES.OFFICE_ADMIN) || scopes.includes(ADMIN_SCOPES.OFFICE_EXEC);
};

export const hasOfficeAccess = user => !!getOfficePermissions(user);

export const canManageOffice = user => {
  const perms = getOfficePermissions(user);
  return perms?.can_manage || false;
};

export const canBookSpace = (user, space) => {
  if (!space?.min_scope) return true;
  const scopes = parseScopes(user);
  const hasAnyOfficeScope = OFFICE_SCOPE_LEVELS.some(l => scopes.includes(l.scope));
  // ALL bypasses min_scope only when the user has no explicit office level.
  // If they were assigned office_manager, that level governs access — ALL doesn't override it.
  // This preserves the intentional hierarchy: an admin who gives someone office_manager
  // expects them to be bound by manager-level access, even if they also have 'all'.
  if (!hasAnyOfficeScope) return scopes.includes(ADMIN_SCOPES.ALL);
  return scopes.includes(space.min_scope);
};

export const isSpaceOnMap = space => {
  return space?.floor_plan_x != null && space?.floor_plan_y != null;
};

export const isRoomType = space => {
  return [OFFICE_SPACE_TYPES.BOARDROOM, OFFICE_SPACE_TYPES.CONFERENCE_ROOM, OFFICE_SPACE_TYPES.SMALL_OFFICE].includes(space?.type);
};

export const getSpaceBookingsAtTime = (space, startTime, endTime) => {
  if (!space?.bookings) return [];
  const selStart = moment(startTime);
  const selEnd = moment(endTime);
  return space.bookings.filter(b => moment(b.starts_at).isBefore(selEnd) && moment(b.ends_at).isAfter(selStart) && b.status === 'confirmed');
};

export const isSpaceAvailable = (space, startTime, endTime) => {
  const activeBookings = getSpaceBookingsAtTime(space, startTime, endTime);
  return activeBookings.length < (space.max_concurrent_bookings || 1);
};

export const ROOM_NAMES = {
  [OFFICE_SPACE_TYPES.BOARDROOM]: ['Board Room'],
  [OFFICE_SPACE_TYPES.CONFERENCE_ROOM]: ['Paris', 'London', 'New York'],
  [OFFICE_SPACE_TYPES.SMALL_OFFICE]: [
    'Los Angeles',
    'Miami',
    'San Francisco',
    'Boston',
    'Chicago',
    'Austin',
    'Toronto',
    'Mexico City',
    'Rio',
    'São Paulo',
    'Milan',
    'Rome',
    'Monaco',
    'Berlin',
    'Madrid',
    'Barcelona',
    'Tokyo',
    'Hong Kong',
    'Dubai',
    'Sydney'
  ]
};

export const TIME_RANGE = { start: 7, end: 21 }; // 7 AM to 9 PM
export const SLOT_MINUTES = 15;
export const POLL_INTERVAL_MS = 10000;

export const BLACKOUT_START = '06-26';
export const BLACKOUT_END = '07-03';

export const isBlackoutDate = dateStr => {
  const d = moment(dateStr).format('MM-DD');
  return d >= BLACKOUT_START && d <= BLACKOUT_END;
};

export const formatDayLabel = dateStr => {
  const d = moment(dateStr);
  const today = moment().startOf('day');
  const diff = d.diff(today, 'days');
  if (diff === 0) return 'Today';
  return d.format('dddd');
};

export const formatDayPossessive = dateStr => `${formatDayLabel(dateStr)}'s`;

export const TOTAL_SLOTS = ((TIME_RANGE.end - TIME_RANGE.start) * 60) / SLOT_MINUTES;

export const timeToSlot = timeStr => {
  const [h, m] = timeStr.split(':').map(Number);
  return Math.round((h * 60 + m - TIME_RANGE.start * 60) / SLOT_MINUTES);
};

export const slotToTimeStr = idx => {
  const totalMin = TIME_RANGE.start * 60 + idx * SLOT_MINUTES;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const slotToLabel = (idx, includeMinutes) => {
  const m = moment(slotToTimeStr(idx), 'HH:mm');
  return m.minutes() === 0 && !includeMinutes ? m.format('h A') : m.format('h:mm A');
};

export const formatTimeClean = isoStr => {
  const m = moment(isoStr);
  return m.minutes() === 0 ? m.format('h A') : m.format('h:mm A');
};

export const formatName = name => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
};

export const inferDeskPeriod = (selectedTime, selectedEndTime) => {
  if (!selectedEndTime) return 'full_day';
  if (selectedTime === '07:00' && selectedEndTime === '13:00') return 'am';
  if (selectedTime === '13:00' && selectedEndTime === '21:00') return 'pm';
  if (selectedTime === '07:00' && selectedEndTime === '21:00') return 'full_day';
  const startMinutes = parseInt(selectedTime.split(':')[0]) * 60 + parseInt(selectedTime.split(':')[1]);
  return startMinutes < 13 * 60 ? 'am' : 'pm';
};

// Builds at least `count` weekdays, extending further when `throughDate` (YYYY-MM-DD) falls past them.
export const buildWeekdays = (count = 50, throughDate = null) => {
  const days = [];
  const today = moment().startOf('day');
  const thisWeekNum = today.isoWeek();
  const nextWeekNum = moment()
    .add(1, 'week')
    .isoWeek();
  const parsedThroughDate = throughDate ? moment(throughDate, 'YYYY-MM-DD') : null;
  const lastDay = parsedThroughDate?.isValid() ? parsedThroughDate.startOf('day') : null;
  for (let i = 0; ; i++) {
    const d = today.clone().add(i, 'days');
    const pastThroughDate = !lastDay || d.isAfter(lastDay);
    if (days.length >= count && pastThroughDate) break;
    if (d.day() === 0 || d.day() === 6) continue;
    const diff = d.diff(today, 'days');
    const wk = d.isoWeek();
    const isThisOrNextWeek = wk === thisWeekNum || wk === nextWeekNum;
    days.push({
      value: d.format('YYYY-MM-DD'),
      weekday: diff === 0 ? 'Today' : isThisOrNextWeek ? d.format('ddd') : d.format('MMM'),
      dayNum: d.format('D'),
      daysOut: diff,
      isoWeek: wk,
      weekBreak: false
    });
  }
  let prev = null;
  days.forEach(d => {
    if (prev && prev.isoWeek !== d.isoWeek) d.weekBreak = true;
    prev = d;
  });
  return days;
};

export const buildQuickPresets = (isToday, nowMoment) => {
  const presets = [];
  const nowHour = nowMoment.hours();
  const nowSlot = Math.max(0, Math.ceil((nowHour * 60 + nowMoment.minutes() - TIME_RANGE.start * 60) / SLOT_MINUTES));

  if (isToday) presets.push({ label: 'Now', start: nowSlot, end: nowSlot + 1 });
  if (!isToday || nowHour < 13) presets.push({ label: 'Morning', start: 0, end: timeToSlot('13:00') });
  if (!isToday || nowHour < 17) presets.push({ label: 'Afternoon', start: timeToSlot('13:00'), end: TOTAL_SLOTS });
  presets.push({ label: 'Full Day', start: 0, end: TOTAL_SLOTS });

  if (isToday && nowSlot < TOTAL_SLOTS) {
    presets.push({ label: 'Rest of Day', start: nowSlot, end: TOTAL_SLOTS });
    const nextHour = nowMoment.minutes() > 0 ? nowHour + 1 : nowHour;
    let count = 0;
    for (let h = nextHour; h < TIME_RANGE.end && count < 4; h++) {
      const s = timeToSlot(`${String(h).padStart(2, '0')}:00`);
      const e = timeToSlot(`${String(h + 1).padStart(2, '0')}:00`);
      if (s >= 0 && e <= TOTAL_SLOTS) {
        presets.push({ label: `${moment({ hour: h }).format('h')}–${moment({ hour: h + 1 }).format('hA')}`, start: s, end: e });
        count++;
      }
    }
  } else if (!isToday) {
    for (let h = 9; h <= 12; h++) {
      const s = timeToSlot(`${String(h).padStart(2, '0')}:00`);
      const e = timeToSlot(`${String(h + 1).padStart(2, '0')}:00`);
      presets.push({ label: `${moment({ hour: h }).format('h')}–${moment({ hour: h + 1 }).format('hA')}`, start: s, end: e });
    }
  }

  return presets;
};
